export const slugify = (str: string) =>
  str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

export function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
  });
}

export const isNotPrivacyString = (link: string) => link !== "Privacy";

export const normalizeURLPath = (path: string) => {
  return path
    .split("/")
    .filter((segment) => segment !== "")
    .join("/")
    .toLowerCase();
};

export const titleize = (slug: string) => {
  if (slug.includes("-")) {
    var words = slug.split("-");
    return words
      .map(function (word) {
        return word.charAt(0).toUpperCase() + word.substring(1).toLowerCase();
      })
      .join(" ");
  } else return toTitleCase(slug);
};

export const setDirectionByNum = (number: number) => {
  if (number % 2 === 0) {
    return "left";
  } else {
    return "right";
  }
};
export const setOffsetByNum = (number: number) => {
  if (number % 2 === 0) {
    return "-300px";
  } else {
    return "300px";
  }
};

// --- Analytics (GA4-first with react-ga fallback) ---
export type GAParam = string | number | boolean | undefined;
export type GAParams = Record<string, GAParam>;

export const AnalyticsCategory = {
  LeadGeneration: "Lead Generation",
  DriveSales: "Drive Sales",
  Engagement: "Engagement",
} as const;
export type AnalyticsCategoryType =
  (typeof AnalyticsCategory)[keyof typeof AnalyticsCategory];

const isBrowser = typeof window !== "undefined";

const getGtag = () => {
  if (!isBrowser) return null;
  const w = window as any;
  return typeof w.gtag === "function"
    ? (w.gtag as (...args: any[]) => void)
    : null;
};

/** Initialize GA4 and optional UA fallback (react-ga) */
export function initAnalytics(opts?: {
  measurementId?: string; // GA4: G-XXXXXXX
  gaTrackingId?: string; // UA-XXXXXXX if you still have UA fallback
  debug?: boolean;
}) {
  if (!isBrowser) return;
  const mid =
    opts?.measurementId || (import.meta as any).env?.VITE_GA_MEASUREMENT_ID;
  if (!mid) return;

  // Inject gtag script if not present
  if (!getGtag()) {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = function gtag() {
      (window as any).dataLayer.push(arguments);
    } as any;
    (window as any).gtag("js", new Date());

    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${mid}`;
    document.head.appendChild(s);
  }

  // Configure GA4; don't auto send page_view to let SPA handle it explicitly
  (window as any).gtag("config", mid, {
    anonymize_ip: true,
    send_page_view: false,
  });
  if (opts?.debug) console.info("[GA] Initialized", mid);
}

function safeEventDispatch(eventName: string, params: GAParams = {}) {
  if (!isBrowser) return;
  const gtag = getGtag();
  if (gtag) {
    try {
      gtag("event", eventName, params);
      return;
    } catch {
      // fall through
    }
  }
  // If GA is blocked and no gtag, log in dev so we can verify wiring without GA
  if ((import.meta as any).env?.DEV) {
    console.debug("[GA dev]", eventName, params);
  }
}

export function trackPageView(path?: string, title?: string) {
  const page_location =
    path ||
    (isBrowser ? window.location.pathname + window.location.search : undefined);
  if (!page_location) return;
  safeEventDispatch("page_view", { page_location, page_title: title });
}

// Domain-specific helpers (GA4-aligned)
export function trackLeadFormSubmit(params: {
  form_name: string;
  lead_type?:
  | "quote_request"
  | "contact_form"
  | "consultation_booking"
  | string;
  product?: string;
  company?: string;
  category?: AnalyticsCategoryType;
  value?: number; // optional monetary value
  source?: string; // e.g., hero, footer, sidebar
}) {
  const { category = AnalyticsCategory.LeadGeneration, ...rest } = params;
  safeEventDispatch("generate_lead", { category, ...rest });
}

export function trackCtaClick(params: {
  cta_label: string; // button text
  cta_location?: string; // e.g., hero, nav, section id
  destination_url?: string;
  category?: AnalyticsCategoryType;
}) {
  const { category = AnalyticsCategory.DriveSales, ...rest } = params;
  // Use GA4 promotion selection semantics for hero/CTA promos
  safeEventDispatch("select_promotion", {
    category,
    promotion_name: rest.cta_label,
    creative_name: rest.cta_location,
    destination_url: rest.destination_url,
  });
}

export function trackContentView(params: {
  content_type: "service_page" | "article" | "case_study" | string;
  content_id?: string;
  title?: string;
  category?: AnalyticsCategoryType;
}) {
  const { category = AnalyticsCategory.Engagement, ...rest } = params;
  safeEventDispatch("view_item", {
    category,
    item_id: rest.content_id,
    item_name: rest.title,
    item_category: rest.content_type,
  });
}

export function trackResourceDownload(params: {
  file_name: string;
  file_extension?: string;
  link_url?: string;
  category?: AnalyticsCategoryType;
}) {
  const { category = AnalyticsCategory.Engagement, ...rest } = params;
  // Matches GA4 Enhanced Measurement semantics
  safeEventDispatch("file_download", { category, ...rest });
}

export function trackVideoPlay(params: {
  video_title: string;
  video_url?: string;
  video_provider?: string;
  category?: AnalyticsCategoryType;
}) {
  const { category = AnalyticsCategory.Engagement, ...rest } = params;
  safeEventDispatch("video_start", { category, ...rest });
}

export function trackNavClick(params: {
  nav_type: "menu" | "footer" | "breadcrumb" | "cta" | string;
  link_text?: string;
  link_url?: string;
  is_external?: boolean;
  category?: AnalyticsCategoryType;
}) {
  const { category = AnalyticsCategory.Engagement, ...rest } = params;
  safeEventDispatch("navigation_click", { category, ...rest });
}

export function trackContactClick(params: {
  method: "phone" | "email" | "sms" | string;
  contact_value?: string; // phone number or email (masked if needed)
  category?: AnalyticsCategoryType;
}) {
  const { category = AnalyticsCategory.LeadGeneration, ...rest } = params;
  safeEventDispatch("contact_click", { category, ...rest });
}

// Back-compat hook; now GA4-first with params
export const useAnalyticsEventTracker = (
  category: AnalyticsCategoryType = AnalyticsCategory.LeadGeneration
) => {
  return (eventName = "custom_event", paramsOrLabel?: GAParams | string) => {
    const params: GAParams =
      typeof paramsOrLabel === "string"
        ? { label: paramsOrLabel }
        : paramsOrLabel || {};
    safeEventDispatch(eventName, { category, ...params });
  };
};
